import React from "react";
import { CalendarX } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { accessExpiredTestIds } from "../testids/authTestIds";

/**
 * Terminal state for a user whose every membership is past its expires_at.
 *
 * The gateway refuses them on every route, so there is no surface left to
 * render — without this they land in the full app chrome with each panel
 * showing "Your access has expired". Sign-out still works: the logout route is
 * exempt from the refusal precisely so a lapsed user can revoke their session.
 *
 * "Check again" is the other half. Being re-invited is the normal way out of
 * this page, and nothing in the browser notices it on its own: the memberships
 * read is keyed on the token, and keycloak.updateToken(60) only mints a new one
 * near the ~300s expiry. Without a button the re-invited user sits on a
 * terminal screen for minutes with only Sign out to press.
 */
export function AccessExpiredPage(): React.ReactElement {
  const { logout, retryMemberships, user } = useAuth();

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen bg-background gap-4 p-6"
      data-testid={accessExpiredTestIds.page}
    >
      <div
        className="max-w-md text-center bg-card rounded-xl p-8 border border-border"
        data-testid={accessExpiredTestIds.card}
      >
        <div className="mb-4 flex justify-center">
          <CalendarX size={48} strokeWidth={1.5} className="text-muted-foreground" />
        </div>
        <h2
          className="text-xl font-semibold text-foreground mb-2"
          data-testid={accessExpiredTestIds.heading}
        >
          Access expired
        </h2>
        <p
          className="text-sm text-muted-foreground leading-relaxed"
          data-testid={accessExpiredTestIds.message}
        >
          {user?.email
            ? `Time-limited access for ${user.email} has ended.`
            : "Your time-limited access has ended."}
        </p>
        <p
          className="text-xs text-muted-foreground mt-4"
          data-testid={accessExpiredTestIds.hint}
        >
          Ask an administrator of your organization to invite you again if you still need access.
        </p>

        <div className="flex flex-col gap-3 mt-6">
          <Button
            onClick={retryMemberships}
            data-testid={accessExpiredTestIds.retryButton}
          >
            Check again
          </Button>
          <Button
            variant="outline"
            onClick={logout}
            data-testid={accessExpiredTestIds.signOutButton}
          >
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
